import { useCategories } from "@/hooks/useCategories";
import { useProducts } from "@/hooks/useProducts";
import { useSiteContent, getContentValue } from "@/hooks/useSiteContent";
import CategoryCard from "./CategoryCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { getCraftStory } from "@/lib/craftStoryContent";

// Fallback images for categories
import terracottaFigurines from "@/assets/terracotta-figurines.webp";
import madhubaniPlates from "@/assets/madhubani-plates.webp";
import ektaraInstruments from "@/assets/ektara-instruments.webp";
import sabaiGrassCrafts from "@/assets/sabai-grass-crafts.webp";
import paintedLanterns from "@/assets/painted-lanterns.webp";
import madhubaniPottery from "@/assets/madhubani-pottery.webp";

const fallbackImages: Record<string, string> = {
  terracotta: terracottaFigurines,
  madhubani: madhubaniPlates,
  "wooden-crafts": ektaraInstruments,
  "sabai-grass": sabaiGrassCrafts,
  "home-decor": paintedLanterns,
  dokra: madhubaniPottery,
  default: terracottaFigurines,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

const CategoriesSection = () => {
  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: products } = useProducts();
  const { data: sectionContent } = useSiteContent("categories_section_content");
  
  const content = sectionContent?.value as Record<string, string> | undefined;
  const badge = getContentValue(content, "badge", "Living Traditions");
  const title = getContentValue(content, "title", "Explore Craft Traditions");
  const description = getContentValue(content, "description", "Each tradition carries its own region, its own rhythm, its own quiet history. Walk slowly through them.");

  // Count products per category
  const getCategoryProductCount = (categoryId: string) => {
    if (!products) return 0;
    return products.filter(p => p.category_id === categoryId).length;
  };

  if (categoriesLoading) {
    return (
      <section className="py-16 md:py-20 bg-gradient-to-b from-background via-secondary/30 to-background">
        <div className="container">
          <div className="text-center mb-10">
            <Skeleton className="h-6 w-40 mx-auto mb-4" />
            <Skeleton className="h-12 w-96 mx-auto mb-4" />
            <Skeleton className="h-5 w-80 mx-auto" />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="aspect-[4/3] rounded-2xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // If no categories in DB, show default ones
  const displayCategories = categories?.length ? categories : [
    { id: "1", name: "Terracotta & Pottery", slug: "terracotta", description: getCraftStory("terracotta").categoryLine, image_url: null },
    { id: "2", name: "Wooden Sculptures", slug: "wooden", description: getCraftStory("wooden").categoryLine, image_url: null },
    { id: "3", name: "Madhubani Art", slug: "madhubani", description: getCraftStory("madhubani").categoryLine, image_url: null },
    { id: "4", name: "Home & Kitchen", slug: "home", description: getCraftStory("home").categoryLine, image_url: null },
  ];

  return (
    <section className="relative py-16 md:py-20 overflow-hidden">
      {/* Rich gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/40 to-background" />
      
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-terracotta/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-0 w-64 h-64 bg-golden/5 rounded-full blur-3xl -translate-y-1/2" />
        
        {/* Warli-style decorative patterns */}
        <svg className="absolute left-8 top-1/4 w-20 h-20 text-earth/[0.06]" viewBox="0 0 100 100" fill="currentColor">
          <circle cx="50" cy="15" r="8" />
          <path d="M50 23 L50 55 M50 35 L30 50 M50 35 L70 50 M50 55 L35 85 M50 55 L65 85" stroke="currentColor" strokeWidth="3" fill="none" />
        </svg>
        <svg className="absolute right-8 bottom-1/4 w-20 h-20 text-earth/[0.06]" viewBox="0 0 100 100" fill="currentColor">
          <circle cx="50" cy="15" r="8" />
          <path d="M50 23 L50 55 M50 35 L30 50 M50 35 L70 50 M50 55 L35 85 M50 55 L65 85" stroke="currentColor" strokeWidth="3" fill="none" />
        </svg>
        <svg className="absolute left-1/2 top-8 w-16 h-16 text-earth/[0.04] -translate-x-1/2" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="50" cy="50" r="25" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="50" cy="50" r="10" fill="currentColor" />
        </svg>
      </div>

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <Badge variant="secondary" className="mb-4 bg-terracotta/10 text-terracotta border-terracotta/20">
            <Sparkles className="h-3 w-3 mr-1.5" />
            {badge}
          </Badge>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            {description}
          </p>
        </motion.div>

        {/* Category Cards Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6"
        >
          {displayCategories.map((category) => {
            const productCount = getCategoryProductCount(category.id);
            const imageUrl = category.image_url || fallbackImages[category.slug] || fallbackImages.default;
            
            return (
              <motion.div key={category.id} variants={itemVariants}>
                <CategoryCard
                  title={category.name}
                  description={category.description || "Discover beautiful handcrafted pieces"}
                  image={imageUrl}
                  href={`/category/${category.slug}`}
                  itemCount={productCount}
                />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default CategoriesSection;